import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import HourReductionDeclarationListing from '../../components/ScientificManagement/HourReductionDeclaration/HourReductionDeclarationListing';
import HourReductionDeclarationForm from '../../components/ScientificManagement/HourReductionDeclaration/HourReductionDeclarationForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { downloadExcel } from '../../utils/excelUtils';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const HourReductionDeclaration = () => {
    const toast = useRef(null);
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const initialForm = useMemo(() => ({
        UserId: '', HourReductionId: '', HourReductionContent: '', ActualPercentage: '', ActualHours: '', StartDate: today, EndDate: today, IsCurrent: false, Notes: ''
    }), [today]);

    const [data, setData] = useState([]);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalMode, setModalMode] = useState(null);
    const isModalOpen = modalMode !== null;
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [selectedDeclaration, setSelectedDeclaration] = useState(null);
    const [editRequestContent, setEditRequestContent] = useState('');

    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectItemId, setRejectItemId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const [filters, setFilters] = useState({
        department: '',
        authorId: null,
        keyword: '',
        status: '',
        fromDate: null,
        toDate: null
    });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        fromDate: null,
        toDate: null,
        reductionContent: '',
        userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    const currentYear = new Date().getFullYear();
    const [adminYear, setAdminYear] = useState(currentYear);
    const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
    const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
    const [snapshotFromDate, setSnapshotFromDate] = useState(null);
    const [snapshotToDate, setSnapshotToDate] = useState(null);

    const [isSnapshotHistoryModalOpen, setIsSnapshotHistoryModalOpen] = useState(false);
    const [snapshotHistory, setSnapshotHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa'),
        [userRoleName, currentUser.RoleId]
    );

    const canModify = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true),
        [userRoleName, currentUser.RoleId, currentUser.IsDelegatedAdmin]
    );

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const filteredData = useMemo(() => {
        let allowedDeptNames = [];
        if (filters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === filters.department.trim());

            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());

                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId === parentId);
                    children.forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };

                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(filters.department.trim());
            }
        }

        return data.filter(item => {
            if (filters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) {
                    return false;
                }
            }

            if (filters.status && item.Status !== filters.status) return false;

            if (item.StartDate) {
                const itemStartDate = new Date(item.StartDate);
                itemStartDate.setHours(0, 0, 0, 0);

                if (filters.fromDate) {
                    const filterFrom = new Date(filters.fromDate);
                    filterFrom.setHours(0, 0, 0, 0);
                    if (itemStartDate < filterFrom) return false;
                }
                if (filters.toDate) {
                    const filterTo = new Date(filters.toDate);
                    filterTo.setHours(0, 0, 0, 0);
                    if (itemStartDate > filterTo) return false;
                }
            } else if (filters.fromDate || filters.toDate) {
                return false;
            }

            if (filters.authorId) {
                if (item.UserId?.toString() !== filters.authorId.toString()) {
                    return false;
                }
            }

            if (filters.keyword) {
                const kw = superCleanString(filters.keyword);
                const lecturerName = superCleanString(item.FullName);
                const lecturerEmail = superCleanString(item.Email);
                const hourContent = superCleanString(item.ReductionContent || item.HourReductionName || item.HourReductionContent);
                const notes = superCleanString(item.Notes || item.Note);

                if (!lecturerName.includes(kw) && !lecturerEmail.includes(kw) && !hourContent.includes(kw) && !notes.includes(kw)) {
                    return false;
                }
            }
            return true;
        });
    }, [data, filters, departments]);

    const fetchDropdowns = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const [uRes, cRes, deptRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers: authHeaders }),
                fetch(`${baseUrl}/researchreduction`, { headers: authHeaders }),
                fetch(`${baseUrl}/department`, { headers: authHeaders })
            ]);
            if (uRes.ok) setUsers(await uRes.json());
            if (cRes.ok) setCategories(await cRes.json());
            if (deptRes.ok) setDepartments(await deptRes.json());
        } catch (error) {
            console.error("Lỗi tải dữ liệu dropdown:", error);
        }
    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            if (!currentUser?.Id) return;
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`,
                {
                    method: 'GET',
                    headers: authHeaders
                }
            );

            if (response.ok) {
                const result = await response.json();
                const mappedResult = result.map(item => ({
                    ...item,
                    HourReductionName: item.HourReductionContent || item.HourReductionName,
                    ActualPercentage: item.ActualPercentage || item.ActualHours,
                    Note: item.Note || ''
                }));
                setData(mappedResult);
            }
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [currentUser?.Id, isManager, authHeaders]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const uniqueReductions = useMemo(() => {
        const contents = data.map(item => item.HourReductionName || item.HourReductionContent).filter(Boolean);
        return [...new Set(contents)].sort();
    }, [data]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        if (name === 'keyword') {
            setSearchQuery(value);
        }
    }, []);

    const handleOpenSnapshotModal = () => {
        setSnapshotFromDate(new Date(adminYear, 0, 1));
        setSnapshotToDate(new Date(adminYear, 11, 31));
        setIsSnapshotModalOpen(true);
    };

    const confirmSnapshot = () => {
        if (!snapshotFromDate || !snapshotToDate) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng chọn đầy đủ Từ ngày và Đến ngày!', life: 3000 });
        }
        if (snapshotFromDate > snapshotToDate) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Từ ngày không được lớn hơn Đến ngày!', life: 3000 });
        }

        setIsSnapshotLoading(true);

        const formatString = (dateObj) => {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration/snapshot`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                year: adminYear,
                fromDate: formatString(snapshotFromDate),
                toDate: formatString(snapshotToDate)
            })
        })
            .then(res => res.json())
            .then(resData => {
                if (resData.status === 'success') {
                    toast.current?.show({ severity: 'success', summary: 'Thành công', detail: resData.message, life: 4000 });
                    setIsSnapshotModalOpen(false);
                    if (isSnapshotHistoryModalOpen) fetchSnapshotHistory();
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: resData.message, life: 4000 });
                }
            })
            .catch(err => {
                toast.current?.show({ severity: 'error', summary: 'Lỗi kết nối', detail: 'Không thể gọi API chốt dữ liệu' });
            })
            .finally(() => setIsSnapshotLoading(false));
    };

    const fetchSnapshotHistory = async () => {
        setIsHistoryLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration/snapshot`, {
                headers: authHeaders
            });
            const result = await res.json();
            if (result.status === 'success') {
                setSnapshotHistory(result.data);
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tải được lịch sử!' });
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const openHistoryModal = () => {
        fetchSnapshotHistory();
        setIsSnapshotHistoryModalOpen(true);
    };

    const handleDeleteSnapshot = (year) => {
        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: `Bạn có chắc chắn muốn xóa dữ liệu đã chốt của năm hành chính ${year}?`,
            accept: async () => {
                try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration/snapshot?year=${year}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });
                    const result = await res.json();
                    if (result.status === 'success') {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: result.message });
                        fetchSnapshotHistory();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message });
                    }
                } catch (err) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối' });
                }
            }
        });
    };

    const handleUpdateSnapshot = (year) => {
        setIsSnapshotHistoryModalOpen(false);
        setAdminYear(year);
        setSnapshotFromDate(new Date(year, 0, 1));
        setSnapshotToDate(new Date(year, 11, 31));
        setIsSnapshotModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (modalMode === 'view') return;

        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền thao tác dữ liệu!', life: 3000 });
        }

        if (editId && data.some(d => d.Id === editId && d.Status === 'Đã duyệt')) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Dữ liệu đã khóa, không thể lưu!', life: 3000 });
        }

        if (!currentUser?.Id) return toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Hết phiên đăng nhập!' });

        let finalContent = formData.HourReductionContent;
        if (formData.HourReductionId) {
            const selectedCategory = categories.find(c => c.Id == formData.HourReductionId || c.id == formData.HourReductionId);
            if (selectedCategory) {
                finalContent = selectedCategory.Name || selectedCategory.name || selectedCategory.HourReductionName;
            }
        }

        const payload = {
            ...formData,
            UserId: parseInt(formData.UserId),
            HourReductionContent: finalContent,
            ActualPercentage: parseInt(formData.ActualPercentage || formData.ActualHours || 0)
        };
        if (editId) payload.Id = editId;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: editId ? 'PUT' : 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                fetchData(true);
                closeModal();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lưu dữ liệu thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép sửa dữ liệu!', life: 3000 });
        }
        if (item.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể sửa hồ sơ đã duyệt!', life: 4000 });
        }

        let matchedCategoryId = item.HourReductionId || '';

        if (item.ReductionContent && categories.length > 0) {
            const matchedCat = categories.find(c => (c.Name || c.name) === item.ReductionContent);
            if (matchedCat) matchedCategoryId = matchedCat.Id || matchedCat.id;
        }

        setEditId(item.Id);
        setFormData({
            ...item,
            UserId: item.UserId || '',
            HourReductionId: matchedCategoryId,
            ReductionContent: item.ReductionContent || '',
            ActualPercentage: item.ActualPercentage || ''
        });
        setModalMode('edit');
    }, [canModify, categories]);

    const handleViewDetail = useCallback((item) => {
        let matchedCategoryId = item.HourReductionId || '';
        if (item.HourReductionContent && categories.length > 0) {
            const matchedCat = categories.find(c => (c.Name || c.name || c.HourReductionName) === item.HourReductionContent);
            if (matchedCat) matchedCategoryId = matchedCat.Id || matchedCat.id;
        }

        setEditId(item.Id);
        setFormData({
            ...item,
            UserId: item.UserId || '',
            HourReductionId: matchedCategoryId,
            ActualPercentage: item.ActualPercentage || item.ActualHours
        });
        setModalMode('view');
    }, [categories]);

    const handleDelete = useCallback((id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }

        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Dữ liệu đang chờ duyệt hoặc đã duyệt không thể xóa!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa mục giảm trừ của giảng viên này?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?id=${id}&userId=${currentUser.Id}&isAdmin=${isManager}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });

                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa dữ liệu!', life: 3000 });
                        fetchData(true);
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
                }
            }
        });
    }, [canModify, data, currentUser.Id, isManager, authHeaders, fetchData, confirmDeleteDialog]);

    const handleRequestDelete = useCallback((id) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem!' });

        confirmDeleteDialog({
            header: 'Yêu cầu Xóa',
            message: 'Vì lý do bảo mật, dữ liệu sẽ không bị xóa ngay. Hệ thống sẽ tạo "Yêu cầu xóa" và cần 1 Admin quản lý khác vào duyệt. Bạn có tiếp tục?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?id=${id}&action=request`, {
                        method: 'DELETE', headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Đã gửi yêu cầu', detail: 'Vui lòng chờ Admin quản lý duyệt xóa!' });
                        fetchData(true);
                    } else {
                        const result = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Thao tác thất bại!' });
                    }
                } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }); }
            }
        });
    }, [canModify, authHeaders, fetchData, confirmDeleteDialog]);

    const handleApproveDelete = useCallback((id) => {
        confirmDeleteDialog({
            header: 'CẢNH BÁO: DUYỆT XÓA VĨNH VIỄN',
            message: 'Hành động này sẽ XÓA VĨNH VIỄN bảng kê khỏi hệ thống. Bạn có chắc chắn muốn duyệt yêu cầu này?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?id=${id}&action=approve`, {
                        method: 'DELETE', headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Đã xóa', detail: 'Dữ liệu đã bị xóa vĩnh viễn.' });
                        fetchData(true);
                    } else {
                        const result = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Từ chối', detail: result.message || 'Thao tác thất bại!' });
                    }
                } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }); }
            }
        });
    }, [authHeaders, fetchData, confirmDeleteDialog]);

    const handleCancelDeleteRequest = useCallback((id) => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?id=${id}&action=cancel`, {
            method: 'DELETE', headers: authHeaders
        }).then(async res => {
            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã hủy yêu cầu xóa.' });
                fetchData(true);
            } else {
                const result = await res.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message });
            }
        }).catch(() => toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }));
    }, [authHeaders, fetchData]);

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các bản ghi đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận tạo yêu cầu xóa hàng loạt',
            message: `Bạn có chắc chắn muốn tạo yêu cầu xóa cho ${ids.length} mục đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?id=${id}&action=request`, {
                            method: 'DELETE', headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã gửi yêu cầu xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi gửi yêu cầu thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleApprove = useCallback(async (id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: id.toString(), status: 'Đã duyệt' })
            });
            if (response.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã phê duyệt bảng kê!' });
                fetchData(true);
            }
        } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Phê duyệt thất bại!' }); }
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData]);

    const handleCancelApprove = useCallback(async (id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền hủy duyệt!', life: 3000 });
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: id.toString(), status: 'Đang chỉnh sửa' })
            });
            if (response.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã hủy duyệt bảng kê!' });
                fetchData(true);
            }
        } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Hủy duyệt thất bại!' }); }
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData]);

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đang chỉnh sửa') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: id.toString(), status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectClick = useCallback((id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền từ chối!', life: 3000 });
        }
        setRejectItemId(id);
        setRejectReason('');
        setIsRejectModalOpen(true);
    }, [canModify]);

    const handleConfirmReject = async () => {
        if (!rejectReason.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do từ chối!', life: 3000 });
            return;
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: rejectItemId.toString(), status: 'Từ chối', note: rejectReason })
            });
            if (response.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã từ chối bảng kê và ghi nhận lý do!' });
                setIsRejectModalOpen(false);
                fetchData(true);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Từ chối thất bại!' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    };

    const handleToggleCurrent = useCallback(async (item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền thay đổi trạng thái áp dụng!', life: 3000 });
        }

        try {
            const payload = { ...item, IsCurrent: !item.IsCurrent };
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PUT',
                headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã cập nhật trạng thái áp dụng!', life: 3000 });
                fetchData(true);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData]);

    const handleOpenRequestEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền yêu cầu chỉnh sửa!', life: 3000 });
        }
        setSelectedDeclaration(item);
        setEditRequestContent('');
        setIsEditRequestModalOpen(true);
    }, [canModify]);

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        if (!editRequestContent.trim()) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do!', life: 3000 });
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hourreductiondeclaration?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: selectedDeclaration.Id.toString(), status: 'Yêu cầu chỉnh sửa', note: editRequestContent })
            });
            if (response.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã gửi Email thông báo cho giảng viên!' });
                setIsEditRequestModalOpen(false);
                fetchData(true);
            }
        } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Gửi yêu cầu thất bại!' }); }
    };

    const closeModal = useCallback(() => {
        setModalMode(null);
        setFormData(initialForm);
        setEditId(null);
    }, [initialForm]);

    const executeExport = useCallback((dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const formattedData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Giảng viên': item.FullName || '',
            'Email': item.Email || '',
            'Đơn vị': item.DepartmentName || '',
            'Nội dung giảm trừ': item.HourReductionName || item.HourReductionContent || '',
            'Phần trăm/Giờ thực tế': item.ActualPercentage || item.ActualHours || 0,
            'Từ ngày': item.StartDate ? new Date(item.StartDate).toLocaleDateString('vi-VN') : '',
            'Đến ngày': item.EndDate ? new Date(item.EndDate).toLocaleDateString('vi-VN') : '',
            'Trạng thái áp dụng': item.IsCurrent ? 'Đang áp dụng' : 'Hết hạn',
            'Trạng thái duyệt': item.Status || '',
            'Ghi chú': item.Notes || '',
            'Ghi chú duyệt': item.Note || ''
        }));

        const summaryRow = {
            'STT': '', 'Giảng viên': 'TỔNG CỘNG:', 'Email': `${dataToExport.length} Bản ghi`,
            'Đơn vị': '', 'Nội dung giảm trừ': '', 'Phần trăm/Giờ thực tế': '',
            'Từ ngày': '', 'Đến ngày': '', 'Trạng thái áp dụng': '', 'Trạng thái duyệt': '', 'Ghi chú': '', 'Ghi chú duyệt': ''
        };

        const colWidths = [
            { wch: 5 }, { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 45 },
            { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 30 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `QuanLyGiamTruGioKHD_${fileSuffix}`,
            sheetName: 'GiamTruGioKHD',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = useCallback(() => {
        let result = [...data];
        if (exportFilters.fromDate) {
            const filterFrom = new Date(exportFilters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => item.StartDate && new Date(item.StartDate) >= filterFrom);
        }
        if (exportFilters.toDate) {
            const filterTo = new Date(exportFilters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => item.StartDate && new Date(item.StartDate) <= filterTo);
        }
        if (exportFilters.reductionContent) result = result.filter(item => (item.HourReductionName || item.HourReductionContent) === exportFilters.reductionContent);
        if (exportFilters.userId) result = result.filter(item => item.UserId?.toString() === exportFilters.userId.toString());
        return result;
    }, [data, exportFilters]);

    const handlePreviewAdvancedFilters = () => {
        setPreviewExportData(getFilteredExportData());
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ fromDate: null, toDate: null, reductionContent: '', userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        executeExport(previewExportData || getFilteredExportData(), 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        const targetNodes = parentId === null
            ? departments.filter(d => !d.ParentId || !departments.some(p => p.Id === d.ParentId))
            : departments.filter(d => d.ParentId === parentId);

        return targetNodes.map(d => (
            <React.Fragment key={`filter-dept-${d.Id}`}>
                <option value={d.Name}>
                    {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                </option>
                {renderDeptTree(d.Id, level + 1)}
            </React.Fragment>
        ));
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title"><h2>QUẢN LÝ GIẢM TRỪ GIỜ NCKH TOÀN TRƯỜNG</h2></div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px', alignItems: isDesktop ? 'center' : 'stretch' }}>
                {canModify && (
                    <button
                        className="btn-add-new"
                        onClick={() => { setEditId(null); setModalMode('add'); }}
                        style={{ backgroundColor: '#0056b3', margin: 0, width: isDesktop ? 'auto' : '100%' }}
                    >
                        <i className="fa-solid fa-plus"></i> Thêm mới
                    </button>
                )}

                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%', height: '36px' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH GIẢM TRỪ CỦA GIẢNG VIÊN</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexDirection: isDesktop ? 'none' : 'column',
                    gap: '15px'
                }}>
                    <select name="department" className="form-input" style={{ width: '100%' }} value={filters.department} onChange={handleFilterChange}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <select
                        name="status"
                        className="form-input"
                        style={{ width: '100%' }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">Trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', gridColumn: isDesktop ? 'span 3' : 'none' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input
                            type="text"
                            name="keyword"
                            placeholder="Tìm kiếm nội dung, ghi chú"
                            className="form-input"
                            style={{ width: '100%', paddingLeft: '35px' }}
                            value={filters.keyword}
                            onChange={handleFilterChange}
                        />
                    </div>
                </div>
            </div>

            <HourReductionDeclarationListing
                data={filteredData}
                searchQuery={searchQuery}
                filters={filters}
                onEdit={handleEdit}
                onDelete={handleRequestDelete}
                onRequestDelete={handleRequestDelete}
                onApproveDelete={handleApproveDelete}
                onCancelDeleteRequest={handleCancelDeleteRequest}
                currentUserEmail={currentUser.Email || ''}
                isLoading={isLoading}
                onApprove={handleApprove}
                onRejectClick={handleRejectClick}
                onCancelApprove={handleCancelApprove}
                onToggleCurrent={handleToggleCurrent}
                onRequestEdit={handleOpenRequestEdit}
                onViewDetail={handleViewDetail}
                onBulkApprove={(ids) => handleBulkStatusChange(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkStatusChange(ids, 'Đang chỉnh sửa')}
                onBulkDelete={handleBulkDelete}
            />

            <HourReductionDeclarationForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={modalMode === 'edit' || modalMode === 'view'}
                readOnly={modalMode === 'view'}
                users={users}
                categories={categories}
            />

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa Bảng kê</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Giảng viên: </strong> {selectedDeclaration?.FullName} ({selectedDeclaration?.Email})
                                        <br />
                                        <strong style={{ marginTop: '5px', display: 'inline-block' }}>Nội dung khai: </strong> {selectedDeclaration?.HourReductionName}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần sửa (Hệ thống sẽ gửi qua Email) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="5"
                                        placeholder="Nhập chi tiết yêu cầu sửa lại thông tin hoặc các quyết định"
                                        value={editRequestContent}
                                        onChange={(e) => setEditRequestContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', paddingTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsEditRequestModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isRejectModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '500px' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Từ chối bảng kê</h3>
                            <button className="close-btn" onClick={() => setIsRejectModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lý do từ chối <span style={{ color: 'red' }}>*</span></label>
                                <textarea
                                    className="form-input"
                                    rows="4"
                                    placeholder="Nhập lý do từ chối (giảng viên sẽ nhìn thấy lý do này)..."
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    required
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="btn-cancel" onClick={() => setIsRejectModalOpen(false)}>Hủy</button>
                            <button className="btn-submit" onClick={handleConfirmReject} style={{ backgroundColor: '#e74c3c' }}>Xác nhận từ chối</button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '750px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Áp dụng)</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Áp dụng)</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mục giảm trừ</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.reductionContent} onChange={(e) => setExportFilters({ ...exportFilters, reductionContent: e.target.value })}>
                                        <option value="">Toàn bộ nội dung</option>
                                        {uniqueReductions.map((content, idx) => (
                                            <option key={idx} value={content}>{content}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px', marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Người dùng (Giảng viên)</label>
                                    <SearchableFocusDropdown
                                        value={exportFilters.userId}
                                        options={userOptions}
                                        onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                        placeholder="Toàn bộ giảng viên"
                                        panelUniqueClass="custom-dropdown-panel-hourreduction-export-advanced-filter"
                                        className="custom-dropdown"
                                    />
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th width="150px">Giảng viên</th>
                                                    <th>Mục giảm trừ</th>
                                                    <th width="120px" style={{ textAlign: 'center' }}>% / Giờ thực tế</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => (
                                                        <tr key={item.Id}>
                                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                            <td><div style={{ fontWeight: '500', color: '#333' }}>{item.FullName || '---'}</div></td>
                                                            <td><div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.HourReductionName || item.HourReductionContent}</div></td>
                                                            <td style={{ textAlign: 'center', color: '#e74c3c', fontWeight: 'bold' }}>{item.ActualPercentage || item.ActualHours || 0}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy bản ghi nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isSnapshotModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1000 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '550px' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                                <i className="fa-solid fa-lock" style={{ marginRight: '8px', color: '#d35400' }}></i>
                                Năm hành chính
                            </h3>
                            <button className="close-btn" onClick={() => setIsSnapshotModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <p style={{ marginBottom: '20px', color: '#555', lineHeight: '1.5', fontSize: '14px', backgroundColor: '#fff3cd', padding: '10px 15px', borderRadius: '4px', borderLeft: '4px solid #f39c12' }}>
                                Vui lòng chọn năm và tùy chỉnh mốc thời gian bắt đầu - kết thúc thực tế cho năm hành chính đó. Hệ thống sẽ khóa các hồ sơ đã duyệt trong khoảng thời gian này và đưa vào báo cáo.
                            </p>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#2c3e50' }}>
                                    Năm hành chính cần chốt <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={adminYear}
                                    placeholder="Nhập năm hành chính (VD: 2026)"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setAdminYear(val);
                                        const y = parseInt(val);
                                        if (!isNaN(y) && val.length === 4) {
                                            setSnapshotFromDate(new Date(y, 0, 1));
                                            setSnapshotToDate(new Date(y, 11, 31));
                                        }
                                    }}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    min="1900"
                                    max="9999"
                                />
                            </div>

                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#2c3e50' }}>Từ ngày <span style={{ color: 'red' }}>*</span></label>
                                    <Calendar
                                        value={snapshotFromDate}
                                        onChange={(e) => setSnapshotFromDate(e.value)}
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '10px' }}
                                        appendTo={document.body}
                                        panelStyle={{ zIndex: 99999 }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#2c3e50' }}>Đến ngày <span style={{ color: 'red' }}>*</span></label>
                                    <Calendar
                                        value={snapshotToDate}
                                        onChange={(e) => setSnapshotToDate(e.value)}
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '10px' }}
                                        appendTo={document.body}
                                        panelStyle={{ zIndex: 99999 }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsSnapshotModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="button" className="btn-submit" onClick={confirmSnapshot} disabled={isSnapshotLoading} style={{ backgroundColor: '#d35400', margin: 0 }}>
                                {isSnapshotLoading ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang xử lý</> : 'Xác nhận chốt'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isSnapshotHistoryModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1000 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '800px' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}><i className="fa-solid fa-clock-rotate-left" style={{ marginRight: '8px', color: '#34495e' }}></i> Lịch sử chốt năm hành chính</h3>
                            <button className="close-btn" onClick={() => setIsSnapshotHistoryModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', maxHeight: '60vh', overflowY: 'auto' }}>
                            {isHistoryLoading ? (
                                <p style={{ textAlign: 'center', color: '#555' }}>Đang tải dữ liệu...</p>
                            ) : (
                                <table className="custom-table" style={{ width: '100%', minWidth: '600px', fontSize: '14px', borderCollapse: 'collapse' }}>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                        <tr>
                                            <th style={{ padding: '10px', borderBottom: '2px solid #ddd' }}>Năm hành chính</th>
                                            <th style={{ padding: '10px', borderBottom: '2px solid #ddd' }}>Từ ngày - Đến ngày</th>
                                            <th style={{ textAlign: 'center', padding: '10px', borderBottom: '2px solid #ddd' }}>Số GV</th>
                                            <th style={{ textAlign: 'center', padding: '10px', borderBottom: '2px solid #ddd' }}>Tổng %/Giờ</th>
                                            <th style={{ textAlign: 'center', padding: '10px', borderBottom: '2px solid #ddd' }}>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {snapshotHistory.map((item, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ fontWeight: 'bold', color: '#d35400', padding: '10px' }}>{item.AdminYear}</td>
                                                <td style={{ padding: '10px' }}>{item.FromDate} - {item.ToDate}</td>
                                                <td style={{ textAlign: 'center', padding: '10px' }}>{item.TotalUsers}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 'bold', padding: '10px' }}>{item.TotalPercentage}</td>
                                                <td style={{ textAlign: 'center', padding: '10px' }}>
                                                    <button onClick={() => handleUpdateSnapshot(item.AdminYear)} style={{ border: 'none', background: 'none', color: '#3498db', cursor: 'pointer', marginRight: '15px', fontSize: '16px' }} title="Chốt lại (Cập nhật)">
                                                        <i className="fa-solid fa-rotate"></i>
                                                    </button>
                                                    <button onClick={() => handleDeleteSnapshot(item.AdminYear)} style={{ border: 'none', background: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }} title="Xóa dữ liệu">
                                                        <i className="fa-solid fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {snapshotHistory.length === 0 && (
                                            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Chưa có dữ liệu chốt năm hành chính</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HourReductionDeclaration;